import { useState, useEffect, useRef, useCallback } from 'react';
import { Header } from '../components/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import axios from 'axios';
import { Plus, Trash2, Loader2, Radio, CheckCircle2, XCircle, MinusCircle, Image as ImageIcon, Send, Settings, ExternalLink, ImageOff } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_META = {
    open: { label: 'OPEN', color: '#3B82F6', Icon: Radio },
    tp_hit: { label: 'TP HIT', color: '#10B981', Icon: CheckCircle2 },
    sl_hit: { label: 'SL HIT', color: '#EF4444', Icon: XCircle },
    be: { label: 'BREAKEVEN', color: '#94A3B8', Icon: MinusCircle },
    cancelled: { label: 'CANCELLED', color: '#64748B', Icon: XCircle },
};

const fileUrl = (path) => (path ? `${API}/files/${path}` : null);

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.open;
    const Icon = meta.Icon;
    return (
        <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-semibold tracking-wide"
            style={{
                backgroundColor: `${meta.color}18`,
                color: meta.color,
                border: `1px solid ${meta.color}40`,
            }}
            data-testid={`signal-status-${status}`}
        >
            <Icon className="w-3 h-3" />
            {meta.label}
        </span>
    );
}

export default function AdminSignals() {
    const [signals, setSignals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [createOpen, setCreateOpen] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [tgStatus, setTgStatus] = useState(null);
    const [tgConfigOpen, setTgConfigOpen] = useState(false);
    const [newChatId, setNewChatId] = useState('');
    const [savingChat, setSavingChat] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [uploadingImage, setUploadingImage] = useState(false);
    const fileInputRef = useRef(null);

    const [form, setForm] = useState({
        market: '',
        direction: 'BUY',
        entry_price: '',
        stop_loss: '',
        take_profit: '',
        notes: '',
        image_path: null,
        image_preview: null,
    });

    const resetForm = useCallback(() => {
        setForm({
            market: '',
            direction: 'BUY',
            entry_price: '',
            stop_loss: '',
            take_profit: '',
            notes: '',
            image_path: null,
            image_preview: null,
        });
    }, []);

    const handleFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            toast.error('Please select an image file');
            return;
        }
        setUploadingImage(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const res = await axios.post(`${API}/admin/uploads/signal-image`, fd, {
                withCredentials: true,
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setForm((f) => ({ ...f, image_path: res.data.path, image_preview: URL.createObjectURL(file) }));
            toast.success('Image uploaded');
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.detail || 'Image upload failed');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleCreate = async () => {
        if (!form.market || !form.entry_price || !form.stop_loss || !form.take_profit) {
            toast.error('Fill in all required fields');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                market: form.market,
                direction: form.direction,
                entry_price: parseFloat(form.entry_price),
                stop_loss: parseFloat(form.stop_loss),
                take_profit: parseFloat(form.take_profit),
                notes: form.notes,
                image_url: form.image_path,
            };
            const res = await axios.post(`${API}/admin/signals`, payload, { withCredentials: true });
            setSignals((s) => [res.data, ...s]);
            if (res.data.telegram_error) {
                toast.warning(`Signal created. Telegram: ${res.data.telegram_error}`);
            } else {
                toast.success('Signal posted!');
            }
            setCreateOpen(false);
            resetForm();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create signal');
        } finally {
            setSubmitting(false);
        }
    };

    const handleOutcome = async (signal, status, rrOverride = null) => {
        try {
            const payload = { status };
            if (rrOverride !== null) payload.rr_override = rrOverride;
            const res = await axios.put(`${API}/admin/signals/${signal.id}`, payload, { withCredentials: true });
            setSignals((arr) => arr.map((x) => (x.id === signal.id ? res.data : x)));
            toast.success(`Marked ${STATUS_META[status]?.label}${rrOverride !== null ? ` (${rrOverride}R)` : ''}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Update failed');
        }
    };

    const switchOutcome = (signal, status) => {
        if (!window.confirm(`Switch ${signal.market} from ${STATUS_META[signal.status]?.label} to ${STATUS_META[status]?.label}?\n\nThe correction will also be posted to Telegram.`)) return;
        handleOutcome(signal, status);
    };

    const editR = (signal) => {
        const val = window.prompt(
            `Custom R result for ${signal.market} (e.g. 0.5 if TP1 was secured before reversal, or -0.5 for partial loss):`,
            signal.rr_ratio ?? ''
        );
        if (val === null || val.trim() === '') return;
        const num = parseFloat(val);
        if (Number.isNaN(num)) return toast.error('Enter a valid number');
        handleOutcome(signal, signal.status, num);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await axios.delete(`${API}/admin/signals/${deleteTarget.id}`, { withCredentials: true });
            setSignals((arr) => arr.filter((x) => x.id !== deleteTarget.id));
            toast.success('Signal deleted');
        } catch (e) {
            toast.error('Delete failed');
        } finally {
            setDeleteTarget(null);
        }
    };

    const handleTestTelegram = async () => {
        try {
            const res = await axios.post(`${API}/admin/telegram/test`, {}, { withCredentials: true });
            if (res.data.ok) toast.success('Telegram test sent ✓');
            else toast.error(`Telegram error: ${res.data.error}`);
        } catch {
            toast.error('Telegram test failed');
        }
    };

    const handleSaveChatId = async () => {
        setSavingChat(true);
        try {
            const res = await axios.put(`${API}/admin/telegram/config`, { chat_id: newChatId }, { withCredentials: true });
            setTgStatus(res.data);
            toast.success('Chat ID saved permanently. Telegram will use this on all environments.');
            setTgConfigOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        } finally {
            setSavingChat(false);
        }
    };

    const formatDate = (s) => {
        if (!s) return '—';
        const d = new Date(s);
        return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [sRes, tRes] = await Promise.all([
                    axios.get(`${API}/signals`, { withCredentials: true }),
                    axios.get(`${API}/admin/telegram/status`, { withCredentials: true }),
                ]);
                if (cancelled) return;
                setSignals(sRes.data);
                setTgStatus(tRes.data);
                if (tRes.data?.chat_id) setNewChatId(tRes.data.chat_id);
            } catch (e) {
                if (!cancelled) toast.error('Failed to load signals');
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loading) {
        return (
            <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
                <Header />
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="w-10 h-10 animate-spin" style={{ color: 'var(--primary-color)' }} />
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ backgroundColor: 'var(--background-base)' }}>
            <Header />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-1" style={{ fontFamily: "'Outfit', sans-serif", color: 'var(--text-primary)' }} data-testid="admin-signals-title">
                            Trading Signals
                        </h1>
                        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            Post live setups for your students and Telegram channel
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button variant="outline" onClick={() => setTgConfigOpen(true)} data-testid="telegram-config-btn">
                            <Settings className="w-4 h-4 mr-2" />
                            Telegram
                        </Button>
                        <Button className="btn-primary" onClick={() => setCreateOpen(true)} data-testid="new-signal-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            New Signal
                        </Button>
                    </div>
                </div>

                {/* Telegram status banner */}
                {tgStatus && (
                    <div className="card-surface p-4 mb-6 flex items-center justify-between gap-4 flex-wrap" data-testid="telegram-banner">
                        <div className="flex items-center gap-3">
                            <Send className="w-5 h-5" style={{ color: tgStatus.chat_id_configured ? '#10B981' : '#F59E0B' }} />
                            <div>
                                <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                    Telegram: {tgStatus.bot_configured ? 'Bot ✓' : 'Bot missing'} · Chat ID: <span style={{ color: 'var(--primary-color)' }}>{tgStatus.chat_id || 'not set'}</span>
                                </p>
                                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                    Send any message in your channel after adding the bot, then update chat ID.
                                </p>
                            </div>
                        </div>
                        <Button size="sm" variant="outline" onClick={handleTestTelegram} data-testid="telegram-test-btn">
                            Send Test
                        </Button>
                    </div>
                )}

                {/* Signals table */}
                <div className="card-surface p-0 overflow-hidden">
                    {signals.length === 0 ? (
                        <div className="text-center py-16">
                            <Radio className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
                            <p className="text-base mb-1" style={{ color: 'var(--text-primary)' }}>No signals yet</p>
                            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Click &quot;New Signal&quot; to post your first setup</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="table-header text-xs uppercase tracking-wider">
                                        <th className="text-left py-3 px-4">Signal</th>
                                        <th className="text-left py-3 px-4">Entry / SL / TP</th>
                                        <th className="text-left py-3 px-4">Status</th>
                                        <th className="text-left py-3 px-4">R:R</th>
                                        <th className="text-left py-3 px-4">Posted</th>
                                        <th className="text-right py-3 px-4">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signals.map((s) => (
                                        <tr key={s.id} className="table-row" data-testid={`signal-row-${s.id}`}>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center gap-3">
                                                    {s.image_url ? (
                                                        <a href={fileUrl(s.image_url)} target="_blank" rel="noreferrer" className="block w-12 h-12 rounded overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--border-subtle)' }}>
                                                            <img src={fileUrl(s.image_url)} alt="" className="w-full h-full object-cover" />
                                                        </a>
                                                    ) : (
                                                        <div className="w-12 h-12 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'var(--background-base)', border: '1px solid var(--border-subtle)' }}>
                                                            <ImageOff className="w-5 h-5" style={{ color: 'var(--text-muted)' }} />
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-semibold tracking-tight" style={{ color: 'var(--text-primary)' }}>{s.market}</p>
                                                        <p className="text-xs font-semibold" style={{ color: s.direction === 'BUY' ? '#10B981' : '#EF4444' }}>
                                                            {s.direction}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                                <div className="space-y-0.5 font-mono text-xs">
                                                    <div>Entry: <span style={{ color: 'var(--text-primary)' }}>{s.entry_price}</span></div>
                                                    <div>SL: <span style={{ color: '#EF4444' }}>{s.stop_loss}</span></div>
                                                    <div>TP: <span style={{ color: '#10B981' }}>{s.take_profit}</span></div>
                                                </div>
                                            </td>
                                            <td className="py-4 px-4">
                                                <StatusBadge status={s.status} />
                                                {s.telegram_error && (
                                                    <p className="text-[10px] mt-1" style={{ color: '#F59E0B' }} title={s.telegram_error}>TG: {s.telegram_error.slice(0, 30)}…</p>
                                                )}
                                            </td>
                                            <td className="py-4 px-4 text-sm font-mono">
                                                {s.rr_ratio !== null && s.rr_ratio !== undefined ? (
                                                    <span style={{ color: s.rr_ratio > 0 ? '#10B981' : s.rr_ratio < 0 ? '#EF4444' : 'var(--text-secondary)' }}>
                                                        {s.rr_ratio > 0 ? '+' : ''}{s.rr_ratio}R
                                                    </span>
                                                ) : (
                                                    <span style={{ color: 'var(--text-muted)' }}>—</span>
                                                )}
                                            </td>
                                            <td className="py-4 px-4 text-xs" style={{ color: 'var(--text-muted)' }}>{formatDate(s.created_at)}</td>
                                            <td className="py-4 px-4">
                                                <div className="flex items-center gap-2 justify-end flex-wrap">
                                                    {s.status === 'open' ? (
                                                        <>
                                                            <Button size="sm" onClick={() => handleOutcome(s, 'tp_hit')} style={{ backgroundColor: '#10B981', color: '#fff' }} data-testid={`tp-${s.id}`}>
                                                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> TP
                                                            </Button>
                                                            <Button size="sm" onClick={() => handleOutcome(s, 'sl_hit')} style={{ backgroundColor: '#EF4444', color: '#fff' }} data-testid={`sl-${s.id}`}>
                                                                <XCircle className="w-3.5 h-3.5 mr-1" /> SL
                                                            </Button>
                                                            <Button size="sm" variant="outline" onClick={() => handleOutcome(s, 'be')} data-testid={`be-${s.id}`}>BE</Button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            {['tp_hit', 'sl_hit', 'be'].filter((st) => st !== s.status).map((st) => (
                                                                <Button
                                                                    key={st}
                                                                    size="sm"
                                                                    variant="outline"
                                                                    className="text-xs"
                                                                    onClick={() => switchOutcome(s, st)}
                                                                    style={{ color: st === 'tp_hit' ? '#10B981' : st === 'sl_hit' ? '#EF4444' : 'var(--text-secondary)' }}
                                                                    data-testid={`switch-${st}-${s.id}`}
                                                                >
                                                                    → {STATUS_META[st]?.label}
                                                                </Button>
                                                            ))}
                                                            <Button size="sm" variant="outline" className="text-xs" onClick={() => editR(s)} data-testid={`edit-r-${s.id}`}>
                                                                Edit R
                                                            </Button>
                                                        </>
                                                    )}
                                                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(s)} data-testid={`del-${s.id}`}>
                                                        <Trash2 className="w-4 h-4" style={{ color: '#EF4444' }} />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </main>

            {/* Create Signal Dialog */}
            <Dialog open={createOpen} onOpenChange={(o) => { setCreateOpen(o); if (!o) resetForm(); }}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col p-0" style={{ backgroundColor: 'var(--background-surface)' }}>
                    <DialogHeader className="px-6 pt-6 pb-2 flex-shrink-0">
                        <DialogTitle style={{ color: 'var(--text-primary)' }}>Post New Signal</DialogTitle>
                        <DialogDescription>Visible to approved students and forwarded to Telegram.</DialogDescription>
                    </DialogHeader>

                    <div className="flex-1 overflow-y-auto px-6">
                        <div className="grid grid-cols-2 gap-4 py-2">
                        <div className="col-span-2 sm:col-span-1">
                            <Label>Market / Pair *</Label>
                            <Input value={form.market} onChange={(e) => setForm({ ...form, market: e.target.value })} placeholder="EURUSD, GOLD, BTCUSD" data-testid="signal-market-input" />
                        </div>
                        <div className="col-span-2 sm:col-span-1">
                            <Label>Direction *</Label>
                            <Select value={form.direction} onValueChange={(v) => setForm({ ...form, direction: v })}>
                                <SelectTrigger data-testid="signal-direction-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="BUY">BUY (Long)</SelectItem>
                                    <SelectItem value="SELL">SELL (Short)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Entry *</Label>
                            <Input type="number" step="any" value={form.entry_price} onChange={(e) => setForm({ ...form, entry_price: e.target.value })} data-testid="signal-entry-input" />
                        </div>
                        <div>
                            <Label>Stop Loss *</Label>
                            <Input type="number" step="any" value={form.stop_loss} onChange={(e) => setForm({ ...form, stop_loss: e.target.value })} data-testid="signal-sl-input" />
                        </div>
                        <div className="col-span-2">
                            <Label>Take Profit *</Label>
                            <Input type="number" step="any" value={form.take_profit} onChange={(e) => setForm({ ...form, take_profit: e.target.value })} data-testid="signal-tp-input" />
                        </div>
                        <div className="col-span-2">
                            <Label>Analysis Notes</Label>
                            <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional rationale, structure, key levels…" data-testid="signal-notes-input" />
                        </div>
                        <div className="col-span-2">
                            <Label>Analysis Chart Image</Label>
                            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="signal-image-input" />
                            {form.image_preview ? (
                                <div className="relative rounded overflow-hidden" style={{ border: '1px solid var(--border-subtle)' }}>
                                    <img src={form.image_preview} alt="" className="w-full max-h-48 object-contain" />
                                    <Button size="sm" variant="destructive" className="absolute top-2 right-2" onClick={() => setForm({ ...form, image_path: null, image_preview: null })}>
                                        Remove
                                    </Button>
                                </div>
                            ) : (
                                <Button type="button" variant="outline" className="w-full" disabled={uploadingImage} onClick={() => fileInputRef.current?.click()} data-testid="signal-image-upload-btn">
                                    {uploadingImage ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ImageIcon className="w-4 h-4 mr-2" />}
                                    {uploadingImage ? 'Uploading…' : 'Upload Chart Image'}
                                </Button>
                            )}
                        </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0" style={{ borderColor: 'var(--border-subtle)', backgroundColor: 'var(--background-surface)' }}>
                        <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
                        <Button className="btn-primary" onClick={handleCreate} disabled={submitting} data-testid="signal-submit-btn">
                            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                            Post Signal
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Telegram Config Dialog */}
            <Dialog open={tgConfigOpen} onOpenChange={setTgConfigOpen}>
                <DialogContent style={{ backgroundColor: 'var(--background-surface)' }}>
                    <DialogHeader>
                        <DialogTitle style={{ color: 'var(--text-primary)' }}>Telegram Configuration</DialogTitle>
                        <DialogDescription>
                            Set the Chat ID where signals will be auto-posted. You can use either <b>@channel_username</b> or a numeric ID like <b>-100123456</b>. Make sure your bot is added as an admin to that channel.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label>Bot Status</Label>
                            <p className="text-sm" style={{ color: tgStatus?.bot_configured ? '#10B981' : '#EF4444' }}>
                                {tgStatus?.bot_configured ? '✓ Bot token configured' : '✗ Bot token missing'}
                            </p>
                        </div>
                        <div>
                            <Label>Chat ID *</Label>
                            <Input value={newChatId} onChange={(e) => setNewChatId(e.target.value)} placeholder="@nikayofx_signals or -1001234567890" data-testid="telegram-chatid-input" />
                            <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                                💡 To get the numeric ID: post any message in the channel, then visit{' '}
                                <a href={`https://api.telegram.org/bot${''}/getUpdates`} className="underline" style={{ color: 'var(--primary-color)' }}>
                                    api.telegram.org/bot&lt;TOKEN&gt;/getUpdates <ExternalLink className="w-3 h-3 inline" />
                                </a>
                            </p>
                        </div>
                    </div>
                    <div className="flex justify-end gap-3">
                        <Button variant="outline" onClick={() => setTgConfigOpen(false)}>Cancel</Button>
                        <Button className="btn-primary" onClick={handleSaveChatId} disabled={savingChat || !newChatId} data-testid="telegram-save-btn">
                            {savingChat && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Save
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete confirm */}
            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent style={{ backgroundColor: 'var(--background-surface)' }}>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete signal?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently remove this signal. Telegram message will not be deleted.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
